import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Button,
  Row,
  Col,
  Typography,
} from "antd";
import {
  CreditCardOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import { bankRecDate as convertArrayToDayjs } from "@/views/BankReconciliation/BankRec/dates";
import commonFn from "@/utils/commonFn";
/* ---------------- 组件外的静态内容 ---------------- */
const columnStyle = {
  padding: 20,
  background: "#fff",
  border: "1px solid #f0f0f0",
  borderRadius: 6,
  height: "100%",
};
const STATUS_STYLE = {
  success: {
    title: "Success",
    color: "#52c41a",
    bg: "#f6ffed",
    border: "#b7eb8f",
    Icon: CheckCircleFilled,
  },
  fail: {
    title: "Failed",
    color: "#ff4d4f",
    bg: "#fff2f0",
    border: "#ffccc7",
    Icon: CloseCircleFilled,
  },
};
const SectionTitle = ({ children }) => (
  <div
    style={{
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 20,
      color: "#262626",
    }}
  >
    {children}
  </div>
);
const InfoItem = ({ label, valueStyle, children }) => (
  <div style={{ display: "flex", alignItems: "baseline", marginBottom: 16 }}>
  <div style={{ width: 90, flexShrink: 0, color: "#8c8c8c" }}>{label}</div>
  <div  style={{    fontWeight: 500,   color: "#262626",minWidth: 0,wordBreak: "break-all", ...valueStyle, }} >
    {children}</div> </div>
);
const StatusBanner = ({ success, msg }) => {
const s = success ? STATUS_STYLE.success : STATUS_STYLE.fail;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 12px",
        marginBottom: 20,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 6,
      }}
    >
      <s.Icon style={{ color: s.color, fontSize: 18, marginTop: 2 }} />
      <div>
        <div style={{ color: s.color, fontWeight: 600 }}>{s.title}</div>
        {/* 只有失败时才显示 msg */}
        {!success && (
          <div style={{ color: "#595959", fontSize: 13, marginTop: 2 }}>
            {msg || "Submit failed, please try again."}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- 主组件（单条） ---------------- */

const RecordCreditModal = ({ visible, record, onClose, onSuccess,glChartNameId, }) => {
  const [form] = Form.useForm();
  const [propOptions, setPropOptions] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [unitList, setUnitList] = useState([]);
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [glAcctList, setGlAcctList] = useState([]);
  const [loadingGlAcct, setLoadingGlAcct] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [propId, setPropId] = useState();
  const [submitResult, setSubmitResult] = useState(null);
  useEffect(() => {
    if (!visible || !record) {
      return;
    }
    let active = true;

    form.resetFields();
    form.setFieldsValue({ remark: record.remark || "",checkNo: record.checkNo || "", });
    setSubmitResult(null);
    setPropId(undefined);
    setPropOptions([]);
    setUnitList([]);
    setGlAcctList([]);

    const loadProps = async () => {
      setLoadingProps(true);
      try {
        const res = await http.get(api.getPropByTrustList, {
          params: { trustId: record.trustId },
        });
        const data = res?.data || res || [];
        const options = data.map((item) => ({
          label: item.propName,
          value: item.propId,
          active: item.active,
        }));

        if (!active) {
          return;
        }
        setPropOptions(options);

        // 第一个值作为 Prop 默认值，再加载 Unit 和 GL Account
        const firstProp = options[0]?.value;
        if (firstProp !== undefined && firstProp !== null) {
          setPropId(firstProp);
          form.setFieldsValue({ prop: firstProp });
          await Promise.all([
            getUnitListFun("", firstProp),
            getGlAcctListFn(firstProp),
          ]);
        }
      } catch (err) {
        if (active) {
          message.error("Failed to load property list");
        }
      } finally {
        if (active) {
          setLoadingProps(false);
        }
      }
    };

    loadProps();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, record, form]);

  const handleClose = () => {
    form.resetFields();
    setPropOptions([]);
    setUnitList([]);
    setGlAcctList([]);
    setPropId(undefined);
    setSubmitResult(null);
    onClose?.();
  };

  const handlePropChange = async (value) => {
    setPropId(value);
    form.setFieldsValue({ unit: undefined, glAcct: undefined });
    setUnitList([]);
    setGlAcctList([]);

    if (!value) {
      return;
    }
    await Promise.all([getUnitListFun("", value), getGlAcctListFn(value)]);
  };

  const getUnitListFun = async (content = "", selectedPropId) => {
    if (!selectedPropId) {
      setUnitList([]);
      return;
    }
    try {
      setLoadingUnit(true);
      const response = await http.get(api.unitSearch, {
        params: { content, propId: selectedPropId },
      });
      setUnitList(response?.data || []);
    } catch (err) {
      console.log(err);
      setUnitList([]);
    } finally {
      setLoadingUnit(false);
    }
  };
const getGlAcctListFn = async (selectedPropId) => {
  if (!selectedPropId) {
    setGlAcctList([]);
    return;
  }
  try {
    setLoadingGlAcct(true);
    const response = await http.get(api.getCreditGIAcct, {
      params: {
        propId: selectedPropId,
        // glChartNameId
        // acctType: ["C", "E"].toString(),
        // active: 1,
      },
    });
    if (response?.code === "1") {
      setGlAcctList(
        (response.data || []).map((item) => ({
          value: item.glAcct,
          label: item.glAcctTitle,
          title: item.title,
        })),
      );
    } else {
      setGlAcctList([]);
      // commonFn.modalFailedFn(response);
    }
  } catch (err) {
    console.log(err);
    setGlAcctList([]);
  } finally {
    setLoadingGlAcct(false);
  }
};
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const params = {
        date1: convertArrayToDayjs(record?.date)?.format("YYYY-MM-DD"),
        amount: record?.amount,
        propId: values.prop,
        glAcct: values.glAcct,
        remark: values.remark,
        checkNo: values.checkNo,
        bankId: record?.bankId,
        bankTransNewId: record?.bankTransNewId,
      };
      const res = await http.post(api.getRecordCredit, params);
      const isSuccess = res?.code === "1" || res?.code === 1;
      setSubmitResult({
        success: isSuccess,
        msg: res?.msg,
        ...(isSuccess ? res?.data : {}),
      });
      if (isSuccess) {
        onSuccess?.(res);
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      setSubmitResult({ success: false, msg: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const resultData = submitResult;

  return (
    <Modal
      title={
        <Row justify="start" align="middle" className="model_title">
          <Col>
            <CreditCardOutlined className="model_title_mr10" />
          </Col>
          <Col>
            <Typography.Title level={4} className="mg" style={{ margin: 0 }}>
              CASH RECEIVE
            </Typography.Title>
          </Col>
        </Row>
      }
      open={visible}
      maskClosable={false}
      forceRender
      width="50%"
      styles={{ body: { maxHeight: "70vh", overflowY: "auto", paddingTop: 8 } }}
      onCancel={handleClose}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!!submitResult?.success}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      }
    >
      <div className="model_top" style={{ width: "100%" }}>
            <Form
       form={form}
       layout="horizontal"
       labelCol={{ span: 5 }}
       wrapperCol={{ span: 19 }}
       size="middle"
       style={{ width: "100%" }}
     >
          <Row gutter={24} style={{ width: "100%", margin: 0 }}>
            {/* 左侧：Cash Receive Detail */}
            <Col span={14}>
              <div style={columnStyle}>
                <SectionTitle>Cash Receive Detail</SectionTitle>

                <Form.Item label="Post Date">
                  <Input
                     disabled
                    value={convertArrayToDayjs(record?.date)?.format(
                      "MM/DD/YYYY",
                    )}
                  />
                </Form.Item>
                <Form.Item label="Amount">
                  <InputNumber
                    style={{ width: "100%" }}
                    disabled
                    controls={false}
                    value={record?.amount}
                    formatter={(value) =>
                      commonFn.amountToDollarBracketFn(value)
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="prop"
                  label="Prop"
                  rules={[{ required: true, message: "Property is required!" }]}
                >
                  <Select
                    placeholder="Select Property"
                    options={propOptions}
                    loading={loadingProps}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    onChange={handlePropChange}
                  />
                </Form.Item>

                <Form.Item name="unit" label="Unit">
                  <Select
                    showSearch
                    allowClear
                    loading={loadingUnit}
                    filterOption={false} 
                    disabled={!propId}
                    onSearch={(value) => getUnitListFun(value, propId)}
                    fieldNames={{ value: "unitId", label: "unitName" }}
                    options={unitList}
                  />
                </Form.Item>

                <Form.Item
                  name="glAcct"
                  label="GL Account"
                  rules={[
                    { required: true, message: "GL Account is required!" },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    disabled={!propId}
                    loading={loadingGlAcct}
                    optionFilterProp="label"
                    options={glAcctList}
                  />
                </Form.Item>

                <Form.Item name="remark" label="Remark">
                  <Input placeholder="Enter remark" allowClear />
                </Form.Item>

           <Form.Item label="Check No" name="checkNo">
           <Input placeholder="Check No." allowClear />
           </Form.Item>

                <Form.Item label="Bank">
                  <Input disabled value={record?.bankNameByTrust} />
                </Form.Item>
              </div>
            </Col>

            {/* 右侧：Transaction Information */}
            <Col span={10}>
              <div style={columnStyle}>
                <SectionTitle>Transaction Information</SectionTitle>
                {resultData ? (
                  <>
                    <StatusBanner
                      success={resultData.success}
                      msg={resultData.msg}
                    />
                    {resultData.success && (
                      <>
                        <InfoItem
                          label="Amount"
                          valueStyle={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: "#1677ff",
                          }}
                        >
                          {commonFn.amountToDollarBracketFn(resultData.amount)}
                        </InfoItem>
                        <InfoItem label="Batch">
                          {resultData.batch ?? "-"}
                        </InfoItem>
                        <InfoItem label="Match ID">
                          {resultData.matchId ?? "-"}
                        </InfoItem>
                      </>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      color: "#bfbfbf",
                      fontSize: 14,
                      textAlign: "center",
                      paddingTop: 40,
                    }}
                  >
                    Transaction information will appear after submission.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default RecordCreditModal;
